import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getApiBaseUrl } from '@/lib/apiBaseUrl';
import { toast } from 'sonner';

const API_URL = getApiBaseUrl();

const StorageCleanupPage = () => {
  const [scope, setScope] = useState('garment_images');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState({});
  const [integrityData, setIntegrityData] = useState(null);
  const [integrityLoading, setIntegrityLoading] = useState(false);
  const [associations, setAssociations] = useState([]);
  const [associationsLoading, setAssociationsLoading] = useState(false);
  const [selectedDangling, setSelectedDangling] = useState({});
  const [reattachGarmentId, setReattachGarmentId] = useState('');
  const [selectedDanglingFitting, setSelectedDanglingFitting] = useState({});
  const [reattachCustomerId, setReattachCustomerId] = useState('');
  const [selectedMissingPhotos, setSelectedMissingPhotos] = useState({});

  const selectedList = useMemo(
    () => Object.keys(selectedKeys).filter((key) => selectedKeys[key]),
    [selectedKeys],
  );

  const danglingReferenceRecords = useMemo(
    () => (data?.dangling_records || []).filter((item) => item.kind === 'reference_image' && item.image_id),
    [data],
  );

  const selectedDanglingIds = useMemo(
    () => Object.keys(selectedDangling).filter((id) => selectedDangling[id]),
    [selectedDangling],
  );

  const danglingFittingRecords = useMemo(
    () => (data?.dangling_records || []).filter((item) => item.kind === 'fitting_photo' && item.photo_id),
    [data],
  );

  const selectedDanglingFittingIds = useMemo(
    () => Object.keys(selectedDanglingFitting).filter((id) => selectedDanglingFitting[id]),
    [selectedDanglingFitting],
  );

  const missingPhotoGroups = useMemo(() => {
    const grouped = {};
    (integrityData?.missing_objects || []).forEach((item) => {
      const photoId = item.photo_id;
      if (!photoId) return;
      if (!grouped[photoId]) {
        grouped[photoId] = {
          photo_id: photoId,
          customer_id: item.customer_id,
          missing_kinds: new Set(),
          missing_keys: [],
        };
      }
      if (item.kind) grouped[photoId].missing_kinds.add(item.kind);
      if (item.missing_key) grouped[photoId].missing_keys.push(item.missing_key);
    });
    return Object.values(grouped).map((entry) => ({
      ...entry,
      missing_kinds: Array.from(entry.missing_kinds),
    }));
  }, [integrityData]);

  const selectedMissingPhotoIds = useMemo(
    () => Object.keys(selectedMissingPhotos).filter((id) => selectedMissingPhotos[id]),
    [selectedMissingPhotos],
  );

  const fetchOrphans = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/storage/orphans?scope=${scope}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to fetch orphan objects');
      }
      const payload = await response.json();
      setData(payload);
      setSelectedKeys({});
      setSelectedDangling({});
      setSelectedDanglingFitting({});
      setSelectedMissingPhotos({});
    } catch (error) {
      toast.error(error.message || 'Failed to fetch orphan objects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrphans();
  }, [scope]);

  const runIntegrityCheck = async () => {
    try {
      setIntegrityLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/storage/integrity`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.detail || 'Failed to run storage integrity check');
      }
      setIntegrityData(payload);
      setSelectedMissingPhotos({});
      if ((payload.missing_objects_count || 0) > 0) {
        toast.error(`Integrity check found ${payload.missing_objects_count} missing object reference(s)`);
      } else {
        toast.success('Integrity check complete. No missing fitting photo objects found.');
      }
    } catch (error) {
      toast.error(error.message || 'Integrity check failed');
    } finally {
      setIntegrityLoading(false);
    }
  };

  const fetchAssociations = async () => {
    try {
      setAssociationsLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/storage/fitting-photo-associations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.detail || 'Failed to load fitting photo associations');
      }
      setAssociations(payload.associations || []);
    } catch (error) {
      toast.error(error.message || 'Failed to load fitting photo associations');
    } finally {
      setAssociationsLoading(false);
    }
  };

  useEffect(() => {
    runIntegrityCheck();
    fetchAssociations();
  }, []);

  const toggleSelect = (key) => {
    setSelectedKeys((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const selectAllVisible = () => {
    const updates = {};
    (data?.orphans || []).forEach((item) => {
      updates[item.key] = true;
    });
    setSelectedKeys(updates);
  };

  const clearSelection = () => setSelectedKeys({});

  const toggleDanglingSelect = (imageId) => {
    setSelectedDangling((prev) => ({ ...prev, [imageId]: !prev[imageId] }));
  };

  const selectAllDangling = () => {
    const updates = {};
    danglingReferenceRecords.forEach((item) => {
      updates[item.image_id] = true;
    });
    setSelectedDangling(updates);
  };

  const clearDanglingSelection = () => setSelectedDangling({});

  const toggleDanglingFittingSelect = (photoId) => {
    setSelectedDanglingFitting((prev) => ({ ...prev, [photoId]: !prev[photoId] }));
  };

  const selectAllDanglingFitting = () => {
    const updates = {};
    danglingFittingRecords.forEach((item) => {
      updates[item.photo_id] = true;
    });
    setSelectedDanglingFitting(updates);
  };

  const clearDanglingFittingSelection = () => setSelectedDanglingFitting({});

  const toggleMissingPhotoSelect = (photoId) => {
    setSelectedMissingPhotos((prev) => ({ ...prev, [photoId]: !prev[photoId] }));
  };

  const selectAllMissingPhotos = () => {
    const updates = {};
    missingPhotoGroups.forEach((item) => {
      updates[item.photo_id] = true;
    });
    setSelectedMissingPhotos(updates);
  };

  const clearMissingPhotoSelection = () => setSelectedMissingPhotos({});

  const handleDeleteSelected = async () => {
    if (selectedList.length === 0) {
      toast.error('Select orphan objects to delete');
      return;
    }
    const confirmed = window.confirm(`Delete ${selectedList.length} selected orphan object(s)?`);
    if (!confirmed) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/storage/orphans/delete`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ keys: selectedList }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.detail || 'Failed to delete selected orphans');
      }

      toast.success(`Deleted ${payload.deleted_count || 0} orphan object(s)`);
      await fetchOrphans();
    } catch (error) {
      toast.error(error.message || 'Delete operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDanglingRecords = async () => {
    if (!selectedDanglingIds.length) {
      toast.error('Select dangling records to delete');
      return;
    }

    const confirmed = window.confirm(`Delete ${selectedDanglingIds.length} selected dangling garment image record(s) and related objects?`);
    if (!confirmed) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/storage/dangling/reference-images/delete`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ image_ids: selectedDanglingIds, delete_objects: true }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.detail || 'Failed to delete dangling records');
      }

      toast.success(`Deleted ${payload.deleted_records || 0} dangling record(s)`);
      await fetchOrphans();
    } catch (error) {
      toast.error(error.message || 'Delete dangling records failed');
    } finally {
      setLoading(false);
    }
  };

  const handleReattachDanglingRecords = async () => {
    if (!selectedDanglingIds.length) {
      toast.error('Select dangling records to reattach');
      return;
    }
    if (!reattachGarmentId.trim()) {
      toast.error('Enter target garment ID to reattach');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/storage/dangling/reference-images/reattach`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ image_ids: selectedDanglingIds, garment_id: reattachGarmentId.trim() }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.detail || 'Failed to reattach dangling records');
      }

      toast.success(`Reattached ${payload.updated || 0} record(s) to garment ${reattachGarmentId.trim()}`);
      await fetchOrphans();
      setReattachGarmentId('');
    } catch (error) {
      toast.error(error.message || 'Reattach operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDanglingFittingRecords = async () => {
    if (!selectedDanglingFittingIds.length) {
      toast.error('Select dangling fitting photo records to delete');
      return;
    }

    const confirmed = window.confirm(`Delete ${selectedDanglingFittingIds.length} selected dangling fitting photo record(s) and related objects?`);
    if (!confirmed) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/storage/dangling/fitting-photos/delete`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ photo_ids: selectedDanglingFittingIds, delete_objects: true }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.detail || 'Failed to delete dangling fitting photo records');
      }

      toast.success(`Deleted ${payload.deleted_records || 0} dangling fitting record(s)`);
      await fetchOrphans();
      await fetchAssociations();
    } catch (error) {
      toast.error(error.message || 'Delete dangling fitting records failed');
    } finally {
      setLoading(false);
    }
  };

  const handleReattachDanglingFittingRecords = async () => {
    if (!selectedDanglingFittingIds.length) {
      toast.error('Select dangling fitting photo records to reattach');
      return;
    }
    if (!reattachCustomerId.trim()) {
      toast.error('Enter target customer_id to reattach');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/storage/fitting-photo-associations/relink`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ photo_ids: selectedDanglingFittingIds, customer_id: reattachCustomerId.trim() }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.detail || 'Failed to reattach dangling fitting photo records');
      }

      toast.success(`Reattached ${payload.updated || 0} fitting record(s) to customer ${reattachCustomerId.trim()}`);
      setReattachCustomerId('');
      await fetchOrphans();
      await fetchAssociations();
    } catch (error) {
      toast.error(error.message || 'Reattach dangling fitting records failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreMissingPhotos = async () => {
    if (!selectedMissingPhotoIds.length) {
      toast.error('Select missing photo associations to restore');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/storage/missing/fitting-photos/restore`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ photo_ids: selectedMissingPhotoIds }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.detail || 'Failed to restore missing fitting photo objects');
      }

      toast.success(`Restored files=${payload.restored_files || 0}, thumbnails=${payload.restored_thumbnails || 0}`);
      await runIntegrityCheck();
      await fetchAssociations();
    } catch (error) {
      toast.error(error.message || 'Restore operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMissingPhotoRecords = async () => {
    if (!selectedMissingPhotoIds.length) {
      toast.error('Select missing photo associations to delete');
      return;
    }

    const confirmed = window.confirm(`Delete ${selectedMissingPhotoIds.length} missing fitting photo record(s)?`);
    if (!confirmed) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/storage/missing/fitting-photos/delete-records`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ photo_ids: selectedMissingPhotoIds }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.detail || 'Failed to delete missing fitting photo records');
      }

      toast.success(`Deleted ${payload.deleted_records || 0} missing fitting photo record(s)`);
      await runIntegrityCheck();
      await fetchAssociations();
      await fetchOrphans();
    } catch (error) {
      toast.error(error.message || 'Delete missing records failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="storage-cleanup-page">
      <div className="flex flex-col gap-2">
        <h1 className="text-3xl font-bold">Storage Cleanup</h1>
        <p className="text-muted-foreground">
          Review orphaned objects in Cloudflare R2 and delete selected items safely.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-3">
            <span data-testid="storage-cleanup-scan-title">Orphan Scan</span>
            <Badge variant="outline" data-testid="storage-cleanup-env-badge">
              {data?.environment || 'dev'}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              className="border rounded px-3 py-2 bg-background"
              data-testid="storage-cleanup-scope-select"
            >
              <option value="garment_images">Garment Images</option>
              <option value="fitting_photos">Fitting Photos</option>
              <option value="all">All</option>
            </select>
            <Button onClick={fetchOrphans} disabled={loading} data-testid="storage-cleanup-refresh-button">
              {loading ? 'Scanning…' : 'Refresh Scan'}
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
            <div className="border rounded p-3" data-testid="storage-cleanup-count-objects">
              Objects Scanned: <strong>{data?.counts?.objects_scanned ?? 0}</strong>
            </div>
            <div className="border rounded p-3" data-testid="storage-cleanup-count-referenced">
              Referenced: <strong>{data?.counts?.referenced_objects ?? 0}</strong>
            </div>
            <div className="border rounded p-3" data-testid="storage-cleanup-count-orphans">
              Orphans: <strong>{data?.counts?.orphans ?? 0}</strong>
            </div>
            <div className="border rounded p-3" data-testid="storage-cleanup-count-dangling">
              Dangling Records: <strong>{data?.counts?.dangling_records ?? 0}</strong>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={selectAllVisible} disabled={!data?.orphans?.length} data-testid="storage-cleanup-select-all-button">
              Select All
            </Button>
            <Button variant="outline" onClick={clearSelection} data-testid="storage-cleanup-clear-selection-button">
              Clear Selection
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteSelected}
              disabled={loading || selectedList.length === 0}
              data-testid="storage-cleanup-delete-selected-button"
            >
              Delete Selected ({selectedList.length})
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle data-testid="storage-cleanup-orphan-list-title">Orphan Objects</CardTitle>
        </CardHeader>
        <CardContent>
          {!data?.orphans?.length ? (
            <p className="text-muted-foreground" data-testid="storage-cleanup-empty-state">No orphan objects found for current scope.</p>
          ) : (
            <div className="overflow-auto border rounded">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 border-b">
                  <tr>
                    <th className="text-left p-2">Select</th>
                    <th className="text-left p-2">Scope</th>
                    <th className="text-left p-2">Key</th>
                    <th className="text-left p-2">Size</th>
                    <th className="text-left p-2">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {data.orphans.map((item) => (
                    <tr key={item.key} className="border-b last:border-b-0" data-testid={`storage-cleanup-orphan-row-${encodeURIComponent(item.key)}`}>
                      <td className="p-2">
                        <input
                          type="checkbox"
                          checked={!!selectedKeys[item.key]}
                          onChange={() => toggleSelect(item.key)}
                          data-testid={`storage-cleanup-orphan-checkbox-${encodeURIComponent(item.key)}`}
                        />
                      </td>
                      <td className="p-2">{item.scope}</td>
                      <td className="p-2 font-mono break-all">{item.key}</td>
                      <td className="p-2">{item.size}</td>
                      <td className="p-2">{item.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle data-testid="storage-cleanup-dangling-title">Dangling Garment Image Records</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!danglingReferenceRecords.length ? (
            <p className="text-muted-foreground" data-testid="storage-cleanup-dangling-empty-state">No dangling garment image records found.</p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" onClick={selectAllDangling} data-testid="storage-cleanup-dangling-select-all">Select All</Button>
                <Button variant="outline" onClick={clearDanglingSelection} data-testid="storage-cleanup-dangling-clear">Clear Selection</Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteDanglingRecords}
                  disabled={loading || selectedDanglingIds.length === 0}
                  data-testid="storage-cleanup-dangling-delete-selected"
                >
                  Delete Selected ({selectedDanglingIds.length})
                </Button>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="text"
                  value={reattachGarmentId}
                  onChange={(e) => setReattachGarmentId(e.target.value)}
                  placeholder="Target garment_id"
                  className="border rounded px-3 py-2 min-w-[320px] bg-background"
                  data-testid="storage-cleanup-dangling-reattach-garment-input"
                />
                <Button
                  onClick={handleReattachDanglingRecords}
                  disabled={loading || selectedDanglingIds.length === 0}
                  data-testid="storage-cleanup-dangling-reattach-selected"
                >
                  Reattach Selected
                </Button>
              </div>

              <div className="overflow-auto border rounded" data-testid="storage-cleanup-dangling-table">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 border-b">
                    <tr>
                      <th className="text-left p-2">Select</th>
                      <th className="text-left p-2">Image ID</th>
                      <th className="text-left p-2">Current Garment ID</th>
                      <th className="text-left p-2">Keys</th>
                      <th className="text-left p-2">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {danglingReferenceRecords.map((item) => (
                      <tr key={item.image_id} className="border-b last:border-b-0" data-testid={`storage-cleanup-dangling-row-${item.image_id}`}>
                        <td className="p-2">
                          <input
                            type="checkbox"
                            checked={!!selectedDangling[item.image_id]}
                            onChange={() => toggleDanglingSelect(item.image_id)}
                            data-testid={`storage-cleanup-dangling-checkbox-${item.image_id}`}
                          />
                        </td>
                        <td className="p-2 font-mono break-all">{item.image_id}</td>
                        <td className="p-2 font-mono break-all">{item.garment_id || '—'}</td>
                        <td className="p-2 text-xs">
                          {(item.keys || []).map((key) => (
                            <div key={key} className="font-mono break-all">{key}</div>
                          ))}
                        </td>
                        <td className="p-2">{item.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle data-testid="storage-cleanup-dangling-fitting-title">Dangling Fitting Photo Records</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!danglingFittingRecords.length ? (
            <p className="text-muted-foreground" data-testid="storage-cleanup-dangling-fitting-empty-state">No dangling fitting photo records found.</p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" onClick={selectAllDanglingFitting} data-testid="storage-cleanup-dangling-fitting-select-all">Select All</Button>
                <Button variant="outline" onClick={clearDanglingFittingSelection} data-testid="storage-cleanup-dangling-fitting-clear">Clear Selection</Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteDanglingFittingRecords}
                  disabled={loading || selectedDanglingFittingIds.length === 0}
                  data-testid="storage-cleanup-dangling-fitting-delete-selected"
                >
                  Delete Selected ({selectedDanglingFittingIds.length})
                </Button>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="text"
                  value={reattachCustomerId}
                  onChange={(e) => setReattachCustomerId(e.target.value)}
                  placeholder="Target customer_id"
                  className="border rounded px-3 py-2 min-w-[320px] bg-background"
                  data-testid="storage-cleanup-dangling-fitting-reattach-customer-input"
                />
                <Button
                  onClick={handleReattachDanglingFittingRecords}
                  disabled={loading || selectedDanglingFittingIds.length === 0}
                  data-testid="storage-cleanup-dangling-fitting-reattach-selected"
                >
                  Reattach Selected
                </Button>
              </div>

              <div className="overflow-auto border rounded" data-testid="storage-cleanup-dangling-fitting-table">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 border-b">
                    <tr>
                      <th className="text-left p-2">Select</th>
                      <th className="text-left p-2">Photo ID</th>
                      <th className="text-left p-2">Current Customer ID</th>
                      <th className="text-left p-2">Keys</th>
                      <th className="text-left p-2">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {danglingFittingRecords.map((item) => (
                      <tr key={item.photo_id} className="border-b last:border-b-0" data-testid={`storage-cleanup-dangling-fitting-row-${item.photo_id}`}>
                        <td className="p-2">
                          <input
                            type="checkbox"
                            checked={!!selectedDanglingFitting[item.photo_id]}
                            onChange={() => toggleDanglingFittingSelect(item.photo_id)}
                            data-testid={`storage-cleanup-dangling-fitting-checkbox-${item.photo_id}`}
                          />
                        </td>
                        <td className="p-2 font-mono break-all">{item.photo_id}</td>
                        <td className="p-2 font-mono break-all">{item.customer_id || '—'}</td>
                        <td className="p-2 text-xs">
                          {(item.keys || []).map((key) => (
                            <div key={key} className="font-mono break-all">{key}</div>
                          ))}
                        </td>
                        <td className="p-2">{item.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle data-testid="storage-integrity-title">Storage Integrity Check</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={runIntegrityCheck}
              disabled={integrityLoading}
              data-testid="storage-integrity-run-button"
            >
              {integrityLoading ? 'Checking…' : 'Run Integrity Check'}
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div className="border rounded p-3" data-testid="storage-integrity-missing-count">
              Missing fitting photo objects: <strong>{integrityData?.missing_objects_count ?? 0}</strong>
            </div>
            <div className="border rounded p-3" data-testid="storage-integrity-markers-count">
              Marker files healthy: <strong>{integrityData?.marker_files?.filter((m) => m.exists).length ?? 0}</strong> / {integrityData?.marker_files?.length ?? 0}
            </div>
          </div>

          {integrityData?.marker_files?.length ? (
            <div className="overflow-auto border rounded" data-testid="storage-integrity-markers-table">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 border-b">
                  <tr>
                    <th className="text-left p-2">Marker Key</th>
                    <th className="text-left p-2">Exists</th>
                  </tr>
                </thead>
                <tbody>
                  {integrityData.marker_files.map((marker) => (
                    <tr key={marker.key} className="border-b last:border-b-0">
                      <td className="p-2 font-mono break-all">{marker.key}</td>
                      <td className="p-2">{marker.exists ? 'Yes' : 'No'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle data-testid="storage-missing-associations-title">Missing Photo Associations</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!missingPhotoGroups.length ? (
            <p className="text-muted-foreground" data-testid="storage-missing-associations-empty-state">
              No missing photo associations.
            </p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" onClick={selectAllMissingPhotos} data-testid="storage-missing-associations-select-all">Select All</Button>
                <Button variant="outline" onClick={clearMissingPhotoSelection} data-testid="storage-missing-associations-clear">Clear Selection</Button>
                <Button
                  onClick={handleRestoreMissingPhotos}
                  disabled={loading || selectedMissingPhotoIds.length === 0}
                  data-testid="storage-missing-associations-restore-selected"
                >
                  Restore Selected ({selectedMissingPhotoIds.length})
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteMissingPhotoRecords}
                  disabled={loading || selectedMissingPhotoIds.length === 0}
                  data-testid="storage-missing-associations-delete-selected"
                >
                  Delete Selected ({selectedMissingPhotoIds.length})
                </Button>
              </div>

              <div className="overflow-auto border rounded" data-testid="storage-missing-associations-table">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 border-b">
                    <tr>
                      <th className="text-left p-2">Select</th>
                      <th className="text-left p-2">Photo ID</th>
                      <th className="text-left p-2">Customer ID</th>
                      <th className="text-left p-2">Missing Types</th>
                      <th className="text-left p-2">Missing Keys</th>
                    </tr>
                  </thead>
                  <tbody>
                    {missingPhotoGroups.map((item) => (
                      <tr key={item.photo_id} className="border-b last:border-b-0" data-testid={`storage-missing-association-row-${item.photo_id}`}>
                        <td className="p-2">
                          <input
                            type="checkbox"
                            checked={!!selectedMissingPhotos[item.photo_id]}
                            onChange={() => toggleMissingPhotoSelect(item.photo_id)}
                            data-testid={`storage-missing-association-checkbox-${item.photo_id}`}
                          />
                        </td>
                        <td className="p-2 font-mono break-all">{item.photo_id}</td>
                        <td className="p-2 font-mono break-all">{item.customer_id || '—'}</td>
                        <td className="p-2">{item.missing_kinds.join(', ') || '—'}</td>
                        <td className="p-2 text-xs">
                          {item.missing_keys.map((key) => (
                            <div key={key} className="font-mono break-all">{key}</div>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-2" data-testid="storage-associations-title">
            <span>Fitting Photo Associations</span>
            <Button variant="outline" onClick={fetchAssociations} disabled={associationsLoading} data-testid="storage-associations-refresh-button">
              {associationsLoading ? 'Refreshing…' : 'Refresh'}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!associations.length ? (
            <p className="text-muted-foreground" data-testid="storage-associations-empty-state">No fitting photo associations found.</p>
          ) : (
            <div className="overflow-auto border rounded" data-testid="storage-associations-table">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 border-b">
                  <tr>
                    <th className="text-left p-2">Photo ID</th>
                    <th className="text-left p-2">Customer</th>
                    <th className="text-left p-2">Session / Pattern</th>
                    <th className="text-left p-2">File</th>
                    <th className="text-left p-2">Thumbnail</th>
                  </tr>
                </thead>
                <tbody>
                  {associations.map((item) => (
                    <tr key={item.photo_id} className="border-b last:border-b-0" data-testid={`storage-association-row-${item.photo_id}`}>
                      <td className="p-2 font-mono">{item.photo_id}</td>
                      <td className="p-2">
                        <div>{item.customer_name || 'Unknown'}</div>
                        <div className="text-xs text-muted-foreground">{item.customer_id || '—'} {item.customer_no ? `(#${item.customer_no})` : ''}</div>
                      </td>
                      <td className="p-2 text-xs">
                        <div>Session: {item.session_id || '—'}</div>
                        <div>Pattern: {item.pattern_id || '—'}</div>
                      </td>
                      <td className="p-2 text-xs">
                        <div className="font-mono break-all">{item.file_key || '—'}</div>
                        <div>{item.file_exists ? 'Exists' : 'Missing'}</div>
                      </td>
                      <td className="p-2 text-xs">
                        <div className="font-mono break-all">{item.thumbnail_key || '—'}</div>
                        <div>{item.thumbnail_exists ? 'Exists' : 'Missing'}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StorageCleanupPage;
